/**
 * metricCards.js
 *
 * RATIONALE — ARCHITECTURE CHANGE (plain functions → React hooks)
 * ─────────────────────────────────────────────────────────────────
 * The original functions were synchronous and called a local SQLite `database`
 * object. Supabase queries are async, so the data-fetching logic must live inside
 * a React lifecycle (useEffect) that can await promises, manage loading state, and
 * subscribe to realtime events. Converting each card to a custom hook is the
 * cleanest way to encapsulate all three concerns (fetch, state, subscription) while
 * still keeping each function's signature to a single `supabase` parameter.
 *
 * RATIONALE — LOADING STRATEGY (skeleton on first load only)
 * ─────────────────────────────────────────────────────────────────
 * `loading` is set to `true` once on mount and cleared after the first successful
 * fetch. Subsequent realtime-triggered re-fetches update data silently so the UI
 * never flickers. This gives the "stale-while-revalidate" feel: the user always
 * sees something, and the card updates in place when fresh data arrives.
 *
 * RATIONALE — REALTIME (postgres_changes on base tables, not views)
 * ─────────────────────────────────────────────────────────────────
 * Supabase Realtime only works on physical tables, not on views. Each hook
 * subscribes to the underlying tables that feed the relevant view or query. When
 * any of those tables change, the hook silently re-fetches its derived data.
 *
 * RATIONALE — onClick REMOVED
 * ─────────────────────────────────────────────────────────────────
 * Scroll refs (paymentCardRef, indexCardRef) were coupling data logic to DOM
 * concerns. The consumer component can attach its own onClick to <MetricCard>
 * after receiving the card props — keeping these hooks pure data hooks.
 *
 * RATIONALE — v_overdue_transactions_all & v_overdue_indexation VIEWS
 * ─────────────────────────────────────────────────────────────────
 * These views centralise complex SQL (generate_series, window functions, JSON_AGG)
 * that would be impossible to express in the Supabase JS client. We simply SELECT
 * from them and aggregate the JSON `buckets` column client-side.
 *
 * RATIONALE — getInfoRentsCard SIMPLIFICATION
 * ─────────────────────────────────────────────────────────────────
 * Forecasting future rent required two queries and mixed real + projected figures,
 * making the number hard to interpret. The card now shows only verified, recorded
 * transactions for the current calendar year — a clean "cash received YTD" KPI.
 */

import { useState, useEffect, useCallback } from 'react'


// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt = (value, opts = {}) =>
    (value ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0, ...opts })

const today = () => new Date().toISOString().split('T')[0]


// ─── useAlertRentDue ─────────────────────────────────────────────────────────
//
// Reads v_overdue_transactions_all.
// Each row = one overdue period for one lease, with a `buckets` JSON array
// containing { category, default_value } objects.
// We count distinct leases and sum every bucket's default_value across all rows.

export const useAlertRentDue = (supabase) => {
    const [cardProps, setCardProps] = useState(null)
    const [loading, setLoading]     = useState(true)

    const fetchData = useCallback(async () => {
        const { data, error } = await supabase
            .from('v_overdue_transactions_all')
            .select('lease_id, buckets')

        if (error) {
            console.error('[useAlertRentDue]', error)
            setLoading(false)
            return
        }

        const leaseCount = new Set(data.map(r => r.lease_id)).size
        const total = data.reduce(
            (sum, row) => sum + (row.buckets ?? []).reduce((s, b) => s + (b.default_value ?? 0), 0),
            0
        )

        setCardProps({
            title:    'Loyers dus',
            subtitle: `Total de ${fmt(total)} €`,
            metric:   leaseCount,
            icon_obj: require('../../../animations/hover/wired-outline-955-demand-hover-click.json'),
            alert:    true,
            visible:  leaseCount > 0,   // hide card when nothing is overdue
        })
        setLoading(false)
    }, [supabase])

    useEffect(() => {
        fetchData()

        // Realtime: re-fetch whenever transactions or lease data change.
        // transactions_bucket covers rent bucket updates (amount changes, new buckets).
        const channel = supabase
            .channel('metric-rent-due')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'transaction' },         fetchData)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'transactions_bucket' }, fetchData)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'lease' },               fetchData)
            .subscribe()

        return () => supabase.removeChannel(channel)
    }, [fetchData, supabase])

    return { cardProps, loading }
}


// ─── useAlertIndexDue ────────────────────────────────────────────────────────
//
// Reads v_overdue_indexation.
// Each row = one lease due for rent indexation, with a `buckets` JSON array
// containing { default_value_old, default_value_new, … } per rent bucket.
// We aggregate across all leases to get a global average ratio and cash increase.

export const useAlertIndexDue = (supabase) => {
    const [cardProps, setCardProps] = useState(null)
    const [loading, setLoading]     = useState(true)

    const fetchData = useCallback(async () => {
        const { data, error } = await supabase
            .from('v_overdue_indexation')
            .select('buckets')

        if (error) {
            console.error('[useAlertIndexDue]', error)
            setLoading(false)
            return
        }

        const allBuckets = data.flatMap(row => row.buckets ?? [])
        const sumOld  = allBuckets.reduce((s, b) => s + (b.default_value_old ?? 0), 0)
        const sumNew  = allBuckets.reduce((s, b) => s + (b.default_value_new ?? 0), 0)
        const ratio   = sumOld > 0 ? (sumNew / sumOld - 1) : 0
        const increase = sumNew - sumOld
        const count    = data.length   // one row per lease pending indexation

        setCardProps({
            title: 'Révisions loyers',
            subtitle: `Total +${(100 * ratio).toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
            })} %, soit ${fmt(increase)} €`,
            metric:   count,
            icon_obj: require('../../../animations/hover/wired-outline-153-bar-chart-hover-growth.json'),
            alert:    true,
            visible:  count > 0,   // hide card when no indexation is pending
        })
        setLoading(false)
    }, [supabase])

    useEffect(() => {
        fetchData()

        // Realtime: price_index changes when new index values are published;
        // transactions_bucket changes when rents are actually revised.
        const channel = supabase
            .channel('metric-index-due')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'transactions_bucket' }, fetchData)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'price_index' },         fetchData)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'lease' },               fetchData)
            .subscribe()

        return () => supabase.removeChannel(channel)
    }, [fetchData, supabase])

    return { cardProps, loading }
}


// ─── useInfoPropertiesCard ───────────────────────────────────────────────────
//
// Two parallel queries (Promise.all for minimal round-trip time):
//   1. Total property count — uses Supabase `head: true` to get just the count
//      without transferring any row data.
//   2. Active leases — to compute how many distinct properties are currently let.
// The "or" filter replicates: end_date IS NULL OR end_date >= today.

export const useInfoPropertiesCard = (supabase) => {
    const [cardProps, setCardProps] = useState(null)
    const [loading, setLoading]     = useState(true)

    const fetchData = useCallback(async () => {
        const t = today()

        const [
            { count: totalCount, error: errProps },
            { data: leasedData,  error: errLeases },
        ] = await Promise.all([
            supabase
                .from('property')
                .select('*', { count: 'exact', head: true }),
            supabase
                .from('lease')
                .select('property_id')
                .lte('start_date', t)
                .or(`end_date.is.null,end_date.gte.${t}`),
        ])

        if (errProps || errLeases) {
            console.error('[useInfoPropertiesCard]', errProps ?? errLeases)
            setLoading(false)
            return
        }

        const leasedCount = new Set((leasedData ?? []).map(l => l.property_id)).size
        const ratio       = totalCount > 0 ? leasedCount / totalCount : 0

        setCardProps({
            title:    'Biens',
            subtitle: `Dont loués : ${leasedCount} (${fmt(100 * ratio)}%)`,
            metric:   totalCount,
            icon_obj: require('../../../animations/hover/wired-outline-63-home-hover-partial-roll.json'),
            alert:    false,
            visible:  true,
        })
        setLoading(false)
    }, [supabase])

    useEffect(() => {
        fetchData()

        const channel = supabase
            .channel('metric-properties')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'property' }, fetchData)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'lease' },    fetchData)
            .subscribe()

        return () => supabase.removeChannel(channel)
    }, [fetchData, supabase])

    return { cardProps, loading }
}


// ─── useInfoRentsCard ────────────────────────────────────────────────────────
//
// SIMPLIFICATION RATIONALE:
// The original card combined actual past receipts with a future forecast derived
// from `getDueTransactionsForLeaseAgg`. The combined total was ambiguous (partly
// real, partly projected) and required two queries. The simplified version shows
// only verified, recorded rent transactions for the current calendar year — a
// clear "cash received YTD" figure.
//
// QUERY APPROACH — two-step (bucket IDs first, then transactions):
// Supabase's JS client does not expose a reliable way to filter on a joined
// column's nullability in a single request (e.g., WHERE transactions_bucket.lease_id
// IS NOT NULL). We therefore first fetch the IDs of all lease-linked buckets, then
// query transactions belonging to those buckets. Both queries are kept small: the
// first transfers only UUIDs; the second transfers only the `value` column.

export const useInfoRentsCard = (supabase) => {
    const [cardProps, setCardProps] = useState(null)
    const [loading, setLoading]     = useState(true)

    const fetchData = useCallback(async () => {
        const year = new Date().getFullYear()

        // Step 1 — get IDs of all rent buckets (lease-linked only)
        const { data: buckets, error: errBuckets } = await supabase
            .from('transactions_bucket')
            .select('id')
            .not('lease_id', 'is', null)

        if (errBuckets) {
            console.error('[useInfoRentsCard] buckets', errBuckets)
            setLoading(false)
            return
        }

        const bucketIds = (buckets ?? []).map(b => b.id)

        let total = 0

        if (bucketIds.length > 0) {
            // Step 2 — sum all rent transactions in the current calendar year
            const { data: transactions, error: errTx } = await supabase
                .from('transaction')
                .select('value')
                .in('bucket_id', bucketIds)
                .gte('period_from', `${year}-01-01`)
                .lte('period_from', `${year}-12-31`)

            if (errTx) {
                console.error('[useInfoRentsCard] transactions', errTx)
                setLoading(false)
                return
            }

            total = (transactions ?? []).reduce((sum, t) => sum + (t.value ?? 0), 0)
        }

        setCardProps({
            title:    'Loyers perçus',
            subtitle: `Année ${year}`,
            metric:   `${fmt(total)} €`,
            icon_obj: require('../../../animations/hover/wired-outline-948-stock-share-hover-pinch.json'),
            alert:    false,
            visible:  true,
        })
        setLoading(false)
    }, [supabase])

    useEffect(() => {
        fetchData()

        // Only `transaction` needs to be watched: adding/editing a payment is the
        // sole event that changes this card's number.
        const channel = supabase
            .channel('metric-rents')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'transaction' }, fetchData)
            .subscribe()

        return () => supabase.removeChannel(channel)
    }, [fetchData, supabase])

    return { cardProps, loading }
}


// ─── useMetricCards (composite hook) ─────────────────────────────────────────
//
// Runs all four hooks in parallel (React renders them simultaneously) and returns
// a flat array of { cardProps, loading } objects, filtered to only visible cards.
// A card whose cardProps is still null (first render, loading) is kept in the
// array so the parent can render the same number of skeletons throughout loading.

const useMetricCards = (supabase) => {
    const rentDue    = useAlertRentDue(supabase)
    const indexDue   = useAlertIndexDue(supabase)
    const properties = useInfoPropertiesCard(supabase)
    const rents      = useInfoRentsCard(supabase)

    return [rentDue, indexDue, properties, rents].filter(
        ({ cardProps }) => cardProps === null || cardProps.visible
    )
}

export default useMetricCards